import { pgTable, text, serial, integer, boolean, timestamp, json, jsonb, foreignKey, varchar, index, uniqueIndex, numeric, doublePrecision, AnyPgColumn } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema as _createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Helper wrapper per evitare errori TS2322 con drizzle-zod .omit()
const createInsertSchema: (...args: any[]) => z.ZodObject<any> = _createInsertSchema as any;
import { relations } from "drizzle-orm";

// ============================================
// NEW SYSTEM: Profile Types + Subcategories
// ============================================

// Profile Type - Main category for filtering (5 options)
export enum ProfileType {
  COUPLE = "Couple",
  FEMALE = "Female",
  MALE = "Male",
  TRANS = "Trans",
  TRAV = "Trav"
}

// All profile type values for validation
export const PROFILE_TYPES = Object.values(ProfileType);

// Subcategory Macro Groups
export enum SubcategoryMacro {
  SWINGER = "Swinger",
  BDSM = "BDSM",
  LGBTQ = "LGBTQ+",
  LOVER = "Lover"
}

// Subcategories organized by macro group
export const SUBCATEGORIES_BY_MACRO: Record<SubcategoryMacro, string[]> = {
  [SubcategoryMacro.SWINGER]: [
    "Couple", // Auto-assigned to Couple profile type as primary subcategory
    "Lady", "Gentleman", "Hotwife", "Hothusband", "Stag", "Vixen",
    "Bull", "Stud", "Cuckold", "Cuckquean", "Cuckcake", "Cuckoldress"
  ],
  [SubcategoryMacro.BDSM]: [
    // Core D/s
    "Dominant", "Domme", "Domina", "submissive", "Switch",
    // Master/Slave
    "Master", "Mistress", "slave", "Goddess", "Sir", "Miss", "Madame",
    // Age Play
    "Daddy", "Mommy", "little", "babygirl", "babyboy", "Caregiver",
    // Pet Play
    "Handler", "Trainer", "pet", "pup", "puppy", "kitten", "kitty", "pony", "bunny",
    // Brat
    "Brat", "Brat Tamer", "Brat Wrangler",
    // Sadism/Masochism
    "Sadist", "Masochist", "Sadomasochist",
    // Rope/Bondage
    "Rigger", "Rope Bunny", "Rope Top", "Rope Bottom",
    // Primal
    "Primal", "Primal Predator", "Primal Prey", "Hunter", "Huntress",
    // Other common roles
    "Top", "bottom", "Service Top", "Service Bottom",
    "Degrader", "degradee", "Voyeur", "Exhibitionist",
    "Owner", "property", "collared", "Keyholder",
    "Doll", "toy", "fuckdoll", "fucktoy"
  ],
  [SubcategoryMacro.LGBTQ]: [
    "Gay", "Lesbian", "Bisexual", "Pansexual", "Queer", "Fluid"
  ],
  [SubcategoryMacro.LOVER]: [
    "Lover", "Romantic", "Sensual", "Passionate"
  ]
};

// Flat list of all subcategories
export const ALL_SUBCATEGORIES: string[] = Object.values(SUBCATEGORIES_BY_MACRO).flat();

// Get macro group for a subcategory
export function getSubcategoryMacro(subcategory: string): SubcategoryMacro | null {
  for (const [macro, subcategories] of Object.entries(SUBCATEGORIES_BY_MACRO)) {
    if (subcategories.includes(subcategory)) {
      return macro as SubcategoryMacro;
    }
  }
  return null;
}

// Get display name for a user (first subcategory or fallback to profile_type)
// Returns null if no data available, so caller can use fallback (e.g., category)
export function getDisplayName(profileType: string | null | undefined, subcategories: string[] | null | undefined, legacyCategory?: string | null): string | null {
  // If has subcategories, use the first one (primary subcategory)
  if (subcategories && subcategories.length > 0) {
    return subcategories[0];
  }
  // If has profile_type, use it
  if (profileType) {
    return profileType;
  }
  // If has legacy category, map it to new subcategory
  if (legacyCategory && CATEGORY_TO_PROFILE_TYPE_MAP[legacyCategory]) {
    const mapped = CATEGORY_TO_PROFILE_TYPE_MAP[legacyCategory];
    if (mapped.subcategories.length > 0) {
      return mapped.subcategories[0];
    }
    return mapped.profileType;
  }
  // No data available
  return null;
}

// Get default subcategory for profile type (only Couple gets auto-assigned)
export function getDefaultSubcategory(profileType: ProfileType): string[] {
  if (profileType === ProfileType.COUPLE) {
    return ["Couple"];
  }
  return [];
}

// ============================================
// LEGACY: Old User Categories (deprecated, kept for migration)
// ============================================
export enum UserCategory {
  // Swinger categories (🟣 Purple)
  COUPLE = "Couple",
  SINGLE_LADY = "Lady",
  GENTLEMAN = "Gentleman",

  // BDSM categories (🔴 Red)
  DOM_M = "Dom (M)",
  DOM_F = "Dom (F)",
  SUB_M = "Sub (M)",
  SUB_F = "Sub (F)",
  SWITCH_M = "Switch (M)",
  SWITCH_F = "Switch (F)",

  // LGBTQ+ categories (🟠 Orange)
  GAY = "Gay",
  LESBIAN = "Lesbian",
  BISEXUAL_M = "Bisexual (M)",
  BISEXUAL_F = "Bisexual (F)",
  TRANSGENDER = "Transgender",

  // LGBTQ+ new category
  TRAV = "Trav",

  // Lovers categories (💖 Pink)
  LOVER_M = "Lover (M)",
  LOVER_F = "Lover (F)",

  // Admin category
  ADMIN = "Admin"
}

// Mapping from old category to new profile_type + subcategories
export const CATEGORY_TO_PROFILE_TYPE_MAP: Record<string, { profileType: ProfileType; subcategories: string[] }> = {
  "Couple": { profileType: ProfileType.COUPLE, subcategories: ["Couple"] }, // Auto-assign Couple subcategory
  "Lady": { profileType: ProfileType.FEMALE, subcategories: ["Lady"] },
  "Gentleman": { profileType: ProfileType.MALE, subcategories: ["Gentleman"] },
  "Dom (M)": { profileType: ProfileType.MALE, subcategories: ["Dominant"] },
  "Dom (F)": { profileType: ProfileType.FEMALE, subcategories: ["Domme"] },
  "Sub (M)": { profileType: ProfileType.MALE, subcategories: ["submissive"] },
  "Sub (F)": { profileType: ProfileType.FEMALE, subcategories: ["submissive"] },
  "Switch (M)": { profileType: ProfileType.MALE, subcategories: ["Switch"] },
  "Switch (F)": { profileType: ProfileType.FEMALE, subcategories: ["Switch"] },
  "Gay": { profileType: ProfileType.MALE, subcategories: ["Gay"] },
  "Lesbian": { profileType: ProfileType.FEMALE, subcategories: ["Lesbian"] },
  "Bisexual (M)": { profileType: ProfileType.MALE, subcategories: ["Bisexual"] },
  "Bisexual (F)": { profileType: ProfileType.FEMALE, subcategories: ["Bisexual"] },
  "Transgender": { profileType: ProfileType.TRANS, subcategories: [] },
  "Trav": { profileType: ProfileType.TRAV, subcategories: [] },
  "Lover (M)": { profileType: ProfileType.MALE, subcategories: ["Lover"] },
  "Lover (F)": { profileType: ProfileType.FEMALE, subcategories: ["Lover"] },
  "Admin": { profileType: ProfileType.MALE, subcategories: [] } // Admin default to Male
};

// Mapping from old visibility_preferences categories to new profile_types
export function mapOldCategoryToProfileType(oldCategory: string): ProfileType | null {
  const mapping = CATEGORY_TO_PROFILE_TYPE_MAP[oldCategory];
  return mapping ? mapping.profileType : null;
}

// Group status enum
export enum GroupStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected"
}

// Telegram groups table
export const telegramGroups = pgTable("telegram_groups", {
  id: serial("id").primaryKey(),
  groupId: text("group_id").notNull().unique(),
  title: text("title").notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: json("created_by").$type<number[]>().default([]), // Array di ID proprietari
  status: text("status", { enum: [GroupStatus.PENDING, GroupStatus.APPROVED, GroupStatus.REJECTED] })
    .default(GroupStatus.PENDING)
    .notNull(),
  approvedBy: integer("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  rejectionReason: text("rejection_reason"),
  posterUrl: text("poster_url"), // URL/percorso della locandina del gruppo
  posterBase64: text("poster_base64"), // Dati base64 della locandina
  accessLink: text("access_link"), // Link di invito del gruppo Telegram
  city: text("city"), // Città del gruppo
  latitude: text("latitude"), // Latitudine della città
  longitude: text("longitude"), // Longitudine della città
  updatedAt: timestamp("updated_at").defaultNow(), // Timestamp ultimo aggiornamento
  macroCategory: text("macro_category"), // Categoria principale: Swinger, BDSM, LGBTQ+, Mixed
  kinkygramTopicId: integer('kinkygram_topic_id'), // Aggiunto ID del topic di Kinkygram
  // Stripe Connect fields
  stripeAccountId: text("stripe_account_id"), // ID dell'account Stripe per questo gruppo
  stripeOnboardingCompleted: boolean("stripe_onboarding_completed").default(false), // Flag per indicare se l'onboarding è stato completato

  // TON Crypto wallet (for receiving crypto payments)
  tonWalletAddress: text("ton_wallet_address"), // Wallet TON del gruppo/organizzatore
  tonWalletConfigured: boolean("ton_wallet_configured").default(false), // Flag setup completato

  // Bank Transfer (for receiving bank payments)
  bankIban: text("bank_iban"), // IBAN per ricevere bonifici
  bankBeneficiaryName: text("bank_beneficiary_name"), // Nome intestatario IBAN
  bankConfigured: boolean("bank_configured").default(false), // Flag setup completato

  // Publishing control - admin must explicitly publish groups
  isPublished: boolean("is_published").default(false), // Solo i gruppi pubblicati dall'admin sono visibili al pubblico
  admissionRequestId: integer("admission_request_id").references(() => groupAdmissionRequests.id), // Collegamento alla richiesta di ammissione

  // Minimum registered members required to create events
  minMembersRequired: integer("min_members_required").notNull().default(50),
});

// Group admission requests table - Richieste di ammissione gruppi (prima dell'approvazione admin)
export const groupAdmissionRequests = pgTable("group_admission_requests", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(), // Chi richiede l'ammissione
  title: text("title").notNull(), // Denominazione del gruppo
  description: text("description"), // Descrizione (ora obbligatoria)
  cities: json("cities").$type<string[]>().default([]), // Array di città (ora obbligatorie)
  macroCategory: text("macro_category"), // Categoria: Swinger, BDSM, LGBTQ+, Lover
  yearsOfActivity: text("years_of_activity"), // Anni di attività: <1, 1-2, 3-5, 6-10, >10
  eventsOrganizedRange: text("events_organized_range"), // Range eventi organizzati: 0-10, 11-50, 51-100, 101-200, >200
  averageGuestsPerEvent: text("average_guests_per_event"), // Media ospiti per evento: <20, 20-50, 51-100, 101-200, >200
  averageEventsPerYear: text("average_events_per_year"), // Media eventi all'anno: 1-5, 6-12, 13-24, 25-50, >50
  paymentMethods: json("payment_methods").$type<string[]>().default([]), // Metodi di pagamento accettati
  website: text("website"), // Sito web del gruppo
  telegramChannel: text("telegram_channel"), // Link al canale Telegram (se presente)
  telegramGroup: text("telegram_group"), // Link al gruppo Telegram (se presente)
  status: text("status", { enum: [GroupStatus.PENDING, GroupStatus.APPROVED, GroupStatus.REJECTED] })
    .default(GroupStatus.PENDING)
    .notNull(),
  approvedBy: integer("approved_by").references(() => users.id), // Admin che approva
  approvedAt: timestamp("approved_at"), // Quando è stata approvata
  rejectionReason: text("rejection_reason"), // Motivo del rifiuto (se rejected)
  createdGroupId: integer("created_group_id").references(() => telegramGroups.id, { onDelete: "set null" }), // ID del gruppo creato dopo approvazione
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const telegramMessages = pgTable("telegram_messages", {
  id: serial("id").primaryKey(),
  groupId: integer("group_id").references(() => telegramGroups.id, { onDelete: "cascade" }),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }),
  message: text("message").notNull(),
  createdAt: timestamp("created_at").defaultNow()
});

// Interessi selezionabili dagli utenti
export enum UserInterest {
  SCAMBIO_COMPLETO = "Scambio completo",
  SCAMBIO_SOFT = "Scambio soft",
  GIOCO_SINGOLI = "Gioco con Singoli",
  GIOCO_SINGOLE = "Gioco con Singole"
}

// Tipo di foto
export enum PhotoType {
  PUBLIC = "public",
  PRIVATE = "private"
}

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  telegramId: text("telegram_id").unique().notNull(), // Ora richiesto: tutti gli utenti avranno un ID Telegram
  telegramUsername: text("telegram_username"), // Username originale di Telegram (opzionale, non tutti gli utenti Telegram ce l'hanno)
  // Rimosso displayName perché non necessario (usiamo username)
  age: integer("age"),
  birthDate: timestamp("birth_date"), // Data di nascita (priorità su age se presente)
  partnerAge: integer("partner_age"), // Età del partner per le coppie
  partnerBirthDate: timestamp("partner_birth_date"), // Data di nascita del partner

  // NEW: Profile Type System (replaces category for filtering)
  profile_type: text("profile_type"), // "Couple" | "Female" | "Male" | "Trans" | "Trav"
  subcategories: json("subcategories").$type<string[]>().default([]), // Max 3 subcategories from any macro group

  // LEGACY: Old category field (deprecated, kept for backward compatibility during migration)
  category: text("category").notNull(),
  about: text("about"),
  photos: json("photos").$type<number[]>().default([]), // Array di tutti gli ID foto dalla tabella photos
  profile_photo_id: integer("profile_photo_id"), // ID foto profilo corrente dalla tabella photos
  //RIMOSSO TEMPORANEAMENTE: photos_base64_backup (Object Storage funziona correttamente)
  interests: json("interests").$type<string[]>().default([]),
  isAdmin: boolean("is_admin").default(false),
  isCreator: boolean("is_creator").default(false), // Creator profile (userbot panel) enabled
  igTenantId: text("ig_tenant_id"), // link to the ig-bot tenant for this creator
  isInvisible: boolean("is_invisible").default(false), // Toggle invisibilità admin
  isBanned: boolean("is_banned").default(false),
  banReason: text("ban_reason"),
  // Pre-ban (warning) fields
  isPreBanned: boolean("is_pre_banned").default(false), // Flag per warning prima del ban
  preBanReason: text("pre_ban_reason"), // Motivo del pre-ban
  preBannedAt: timestamp("pre_banned_at"), // Data del pre-ban
  preBannedBy: integer("pre_banned_by"), // ID admin che ha dato il pre-ban
  city: text("city"), // Città dell'utente
  latitude: text("latitude"), // Latitudine per posizionamento geografico
  longitude: text("longitude"), // Longitudine per posizionamento geografico
  visibility_preferences: text("visibility_preferences").default('[]'),
  language: varchar("language", { length: 10 }).notNull().default("en"),
  lastSeen: timestamp("last_seen"),
  isOnline: boolean("is_online").notNull().default(false),
  // Validation counts
  metCount: integer("met_count").notNull().default(0),
  playedCount: integer("played_count").notNull().default(0),
  relationshipCount: integer("relationship_count").notNull().default(0),
  // Partner linking (for couples)
  partnerTelegramId: text("partnerTelegramId"), // ID Telegram del partner collegato
  partnerTelegramUsername: text("partnerTelegramUsername"), // Username Telegram del partner (@username)
  // Chat display names (for couples to distinguish who is chatting)
  chatDisplayName: text("chat_display_name"), // Nome da mostrare in chat per l'utente principale
  partnerChatDisplayName: text("partner_chat_display_name"), // Nome da mostrare in chat per il partner
  // Chat validation requirements (privacy settings for who can message)
  chatValidationRequirements: json("chat_validation_requirements").$type<Record<string, number | null>>().default({}), // { "Couple": 5, "Lady": null } - requisiti minimi validazioni per categoria

  // TON Crypto wallet (for users who organize events without groups)
  tonWalletAddress: text("ton_wallet_address"), // Wallet TON personale dell'utente
  tonWalletConfigured: boolean("ton_wallet_configured").default(false), // Flag setup completato
});

// Ban Type enum
export enum BanType {
  BANNED = "banned",                          // Ban permanente per violazione regole
  REJECTED = "rejected",                      // Primo rifiuto (può riprovare dopo 24 ore)
  PERMANENTLY_REJECTED = "permanently_rejected" // Secondo rifiuto (bloccato per sempre)
}

// Tabella banned_rejected_users - Utenti bannati o rifiutati
export const bannedRejectedUsers = pgTable("banned_rejected_users", {
  id: serial("id").primaryKey(),
  originalUserId: integer("original_user_id"), // ID utente originale (null se mai stato in users)

  // User data (tutti i campi di users per poter ripristinare)
  username: text("username").notNull().unique(),
  telegramId: text("telegram_id").unique().notNull(),
  telegramUsername: text("telegram_username"),
  age: integer("age"),
  birthDate: timestamp("birth_date"),
  partnerAge: integer("partner_age"),
  partnerBirthDate: timestamp("partner_birth_date"),
  profile_type: text("profile_type"),
  subcategories: json("subcategories").$type<string[]>().default([]),
  category: text("category").notNull(),
  about: text("about"),
  photos: json("photos").$type<number[]>().default([]),
  profile_photo_id: integer("profile_photo_id"),
  interests: json("interests").$type<string[]>().default([]),
  city: text("city"),
  latitude: text("latitude"),
  longitude: text("longitude"),
  visibility_preferences: text("visibility_preferences").default('[]'),
  language: varchar("language", { length: 10 }).notNull().default("en"),
  metCount: integer("met_count").notNull().default(0),
  playedCount: integer("played_count").notNull().default(0),
  relationshipCount: integer("relationship_count").notNull().default(0),
  partnerTelegramId: text("partnertelegramid"),
  partnerTelegramUsername: text("partnertelegramusername"),
  chatDisplayName: text("chat_display_name"),
  partnerChatDisplayName: text("partner_chat_display_name"),
  chatValidationRequirements: json("chat_validation_requirements").$type<Record<string, number | null>>().default({}),

  // Ban/Rejection specific fields
  banType: text("ban_type", {
    enum: [BanType.BANNED, BanType.REJECTED, BanType.PERMANENTLY_REJECTED]
  }).notNull(),
  rejectionCount: integer("rejection_count").notNull().default(0), // Numero di rifiuti (0=ban diretto, 1=primo rifiuto, 2+=permanente)
  bannedAt: timestamp("banned_at").defaultNow().notNull(),
  bannedBy: integer("banned_by").notNull(), // ID admin che ha fatto il ban/rifiuto
  banReason: text("ban_reason").notNull(),
  canReapplyAt: timestamp("can_reapply_at"), // Data in cui può riprovare (solo per REJECTED, null per gli altri)

  // History
  previousBanHistory: json("previous_ban_history").$type<{
    date: string;
    type: string;
    reason: string;
    bannedBy: number;
  }[]>().default([]), // Storico ban/rifiuti precedenti
});

// Pending Application Status enum
export enum PendingApplicationStatus {
  INITIAL = "initial",      // Utente creato ma non ha ancora inviato il form
  PENDING = "pending",      // Utente ha inviato il form, in attesa di approvazione
  APPROVED = "approved",
  REJECTED = "rejected"
}

// Tabella pending_users - Utenti in attesa di approvazione (non da gruppi)
export const pendingUsers = pgTable("pending_users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull(),
  telegramId: text("telegram_id").unique().notNull(),
  telegramUsername: text("telegram_username"),
  age: integer("age"),
  partnerAge: integer("partner_age"),
  birthDate: timestamp("birth_date"), // Data di nascita utente
  partnerBirthDate: timestamp("partner_birth_date"), // Data di nascita partner (per coppie)

  // NEW: Profile Type System
  profile_type: text("profile_type"), // "Couple" | "Female" | "Male" | "Trans" | "Trav"
  subcategories: json("subcategories").$type<string[]>().default([]), // Max 3 subcategories

  // LEGACY: Old category field (kept for backward compatibility)
  category: text("category").notNull(),
  about: text("about"),
  city: text("city"),
  latitude: text("latitude"),
  longitude: text("longitude"),
  language: varchar("language", { length: 10 }).notNull().default("en"),
  visibility_preferences: text("visibility_preferences").default('[]'), // Categorie selezionate dall'utente

  // Application-specific fields
  applicationNote: text("application_note"), // Info aggiuntive fornite dall'utente
  appsUsed: json("apps_used").$type<string[]>().default([]), // App/community kink utilizzate
  timeInCommunity: text("time_in_community"), // Tempo nella community kink
  lookingFor: json("looking_for").$type<string[]>().default([]), // Cosa cerca su Kinkygram
  howDiscovered: text("how_discovered"), // Come ha scoperto Kinkygram
  appliedAt: timestamp("applied_at").defaultNow().notNull(),
  lastReapplyAt: timestamp("last_reapply_at"), // Ultimo tentativo di richiesta
  status: text("status", { enum: [PendingApplicationStatus.INITIAL, PendingApplicationStatus.PENDING, PendingApplicationStatus.APPROVED, PendingApplicationStatus.REJECTED] })
    .default(PendingApplicationStatus.INITIAL)
    .notNull(),
  rejectionReason: text("rejection_reason"),

  // Telegram message tracking for approval flow
  telegramMessageId: text("telegram_message_id"), // ID del messaggio inviato a @kinkygram
  approvalToken: text("approval_token"), // Token per validare i callback

  // Temporary photos stored in Object Storage (deleted after approval/rejection)
  temporaryPhotos: text("temporary_photos").array().default([]), // Array of Object Storage paths
});

// Insert schema per pending users
export const insertPendingUserSchema = createInsertSchema(pendingUsers).omit({ 
  id: true, 
  appliedAt: true, 
  lastReapplyAt: true,
  status: true,
  rejectionReason: true,
  telegramMessageId: true,
  approvalToken: true,
  temporaryPhotos: true
});

// Types per pending users
export type PendingUser = typeof pendingUsers.$inferSelect;
export type InsertPendingUser = z.infer<typeof insertPendingUserSchema>;

// Tabella delle foto
export const photos = pgTable("photos", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, {
    onDelete: "cascade" // Elimina foto quando l'utente viene eliminato
  }),
  url: text("url").notNull(), // URL pubblico per accesso diretto
  type: text("type", { enum: [PhotoType.PUBLIC, PhotoType.PRIVATE] }).notNull().default(PhotoType.PUBLIC),
  isProfilePhoto: boolean("is_profile_photo").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  order: integer("order").default(0), // Per mantenere l'ordine delle foto
  description: text("description"), // Descrizione della foto
  fileSize: integer("file_size"), // Dimensione del file in bytes
  objectStoragePath: text("object_storage_path"), // Percorso nell'Object Storage
  mimeType: text("mime_type").default("image/jpeg"), // Tipo MIME dell'immagine
  // Manteniamo fileDataBase64 per retrocompatibilità durante la migrazione
  fileDataBase64: text("file_data_base64") // Sarà deprecato e rimosso dopo la migrazione
});

// Partner link tokens table - for secure partner linking
export const partnerLinkTokens = pgTable("partner_link_tokens", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  token: text("token").notNull().unique(), // Token segreto univoco
  createdAt: timestamp("created_at").notNull().defaultNow(),
  expiresAt: timestamp("expires_at").notNull(), // Scadenza 48 ore
  usedAt: timestamp("used_at"), // Quando è stato usato (null = non ancora usato)
  usedByTelegramId: text("used_by_telegram_id"), // Telegram ID di chi ha usato il token
});

// Events tables
export const events = pgTable("events", {
  id: serial("id").primaryKey(),
  organizerId: integer("organizer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  groupId: integer("group_id").references(() => telegramGroups.id),
  title: text("title").notNull(),
  description: text("description"),
  date: timestamp("date").notNull(),
  location: text("location").notNull(),
  city: text("city").notNull(),
  latitude: text("latitude").notNull(),
  longitude: text("longitude").notNull(),
  posterUrl: text("poster_url"),
  dresscodeMan: text("dresscode_man"),
  dresscodeWoman: text("dresscode_woman"),
  categoryLimits: json("category_limits").$type<Record<string, number | null>>().default({}), // { "Couple": 50, "Lady": null }
  categoryPrices: json("category_prices").$type<Record<string, number>>().default({}), // { "Couple": 25.00, "Lady": 15.00 } in EUR
  allowedCategories: json("allowed_categories").$type<string[]>().default([]), // ["Couple", "Gentleman", "Lady"]
  participants: json("participants").$type<number[]>().default([]), // Array degli ID utenti partecipanti
  confirmedParticipants: text('confirmed_participants').default('[]'), // Array degli ID utenti partecipanti confermati
  participationTimestamps: json("participation_timestamps").$type<Record<string, string>>().default({}), // I timestamp di partecipazione
  participantPayments: text('participant_payments').default('{}'), // Pagamenti dei partecipanti { "userId": amount }
  participantPaymentStatus: text('participant_payment_status').default('{}'), // Status dei pagamenti { "userId": "free" | "unpaid" | "paid" }
  participantPaymentMethods: text('participant_payment_methods').default('{}'), // Metodo di pagamento usato { "userId": "stripe" | "ton" }
  manualParticipants: text('manualParticipants').default('{}'), // Partecipanti aggiunti manualmente { "category": [{id, username, category}] }
  manualParticipantPayments: text('manual_participant_payments').default('{}'), // Pagamenti dei partecipanti manuali { "values": {id: amount}, "status": {id: "free"|"unpaid"|"paid"} }
  offAppParticipants: text('off_app_participants').default('[]'), // Persone senza app che "partecipano" ma NON sono in lista/confermate: [{id, category, age?, partnerAge?, city?}]
  expenses: text('expenses').default('[]'), // Spese dell'evento [{ id, description, amount, isPaid }]
  arrivals: text('arrivals').default('{}'), // Check-in degli ospiti { participantId: timestamp }
  price: text('price'), // Campo legacy per il prezzo (deprecated)
  posterBase64: text('poster_base64'), // Campo legacy per poster base64 (deprecated, ora usiamo object storage)
  locationAddress: text("location_address"), // Indirizzo completo della location
  locationCoordinates: text("location_coordinates"), // Coordinate GPS (opzionale)
  addressVisibleHoursBefore: integer("address_visible_hours_before").default(0), // Ore prima dell'evento in cui l'indirizzo diventa visibile (0-24)
  additionalInfo: text("additional_info"), // Info aggiuntive visibili solo quando l'indirizzo è visibile
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  // Pre-calculated vote averages for performance
  avgLocation: numeric('avg_location', { precision: 3, scale: 1 }).default('0'),
  avgSelection: numeric('avg_selection', { precision: 3, scale: 1 }).default('0'),
  avgFoodBeverage: numeric('avg_food_beverage', { precision: 3, scale: 1 }).default('0'),
  avgOrganization: numeric('avg_organization', { precision: 3, scale: 1 }).default('0'),
  avgMusic: numeric('avg_music', { precision: 3, scale: 1 }).default('0'),
  avgAtmosphere: numeric('avg_atmosphere', { precision: 3, scale: 1 }).default('0'),
  totalVotes: integer('total_votes').default(0),
  votesLastUpdated: timestamp('votes_last_updated'),
  publicShareToken: text('public_share_token'), // Token casuale per condivisione pubblica sicura
  publicShareEnabled: boolean('public_share_enabled').default(false), // Indica se la condivisione pubblica è attiva
  publicShareViews: integer('public_share_views').default(0), // Contatore visualizzazioni link condivisione

  // Payment methods configuration (per-event)
  acceptStripe: boolean('accept_stripe').default(true), // Se abilitare pagamenti con carta (Stripe) per questo evento
  acceptCryptoPayments: boolean('accept_crypto_payments').default(false), // Se abilitare pagamenti crypto per questo evento
  cryptoWalletAddress: text('crypto_wallet_address'), // Wallet TON organizzatore per questo evento (override del wallet utente)
  acceptedCryptoTokens: json('accepted_crypto_tokens').$type<string[]>().default(['usdt', 'usdc']), // Token accettati: ['usdt', 'usdc', 'ton']
  acceptTonNative: boolean('accept_ton_native').default(false), // DEPRECATED: usa acceptedCryptoTokens
  acceptBankTransfer: boolean('accept_bank_transfer').default(false), // Se abilitare pagamenti tramite bonifico bancario per questo evento
  isMultiDate: boolean('is_multi_date').default(false), // Se l'evento ha più date con sistema ticket separato

  // Custom comments section configuration
  commentsEnabled: boolean('comments_enabled').default(false),
  commentsTitle: text('comments_title'),
  commentsDescription: text('comments_description'),

  // Date di inizio e fine evento
  startDate: timestamp('start_date'),
  endDate: timestamp('end_date'),
});

// Tabella per le date degli eventi multi-data
export const eventDates = pgTable("event_dates", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  date: timestamp("date").notNull(),
  label: text("label"), // "Venerdì", "Sabato", o etichetta custom
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

// Tabella per i pacchetti ticket degli eventi multi-data
export const eventTickets = pgTable("event_tickets", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  name: text("name").notNull(), // "Venerdì", "Sabato", "Weekend Pass"
  description: text("description"),
  dateIds: json("date_ids").$type<number[]>().default([]), // quali date copre questo ticket
  categoryPrices: json("category_prices").$type<Record<string, number>>().default({}), // prezzi in centesimi per categoria
  categoryLimits: json("category_limits").$type<Record<string, number | null>>().default({}), // limiti posti per categoria (null = illimitati)
  sortOrder: integer("sort_order").default(0),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

// Tabella per le partecipazioni ai ticket (chi ha acquistato quale ticket)
export const eventTicketParticipations = pgTable("event_ticket_participations", {
  id: serial("id").primaryKey(),
  ticketId: integer("ticket_id").notNull().references(() => eventTickets.id, { onDelete: "cascade" }),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }), // denormalizzato per query efficienti
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }), // nullable per guest purchases
  category: text("category").notNull(), // categoria dell'utente al momento dell'acquisto
  paymentStatus: text("payment_status").default("unpaid"), // free, unpaid, paid, pending_verification, pending_payment
  paymentMethod: text("payment_method"), // stripe, ton, bank_transfer
  amountPaid: integer("amount_paid"), // importo in centesimi
  couponId: integer("coupon_id"), // ID coupon usato (se applicabile)
  // Guest purchase fields (when userId is null)
  guestEmail: text("guest_email"),
  guestName: text("guest_name"),
  guestPhone: text("guest_phone"),
  stripeSessionId: text("stripe_session_id"), // per tracciare il pagamento Stripe guest
  createdAt: timestamp("created_at").defaultNow(),
  confirmedAt: timestamp("confirmed_at"), // quando il pagamento è stato confermato
});

// Tabella per le partecipazioni agli eventi
export const eventParticipations = pgTable("event_participations", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }),
  eventId: integer("event_id").references(() => events.id, { onDelete: "cascade" }),
  participating: boolean("participating").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Tabella per i voti degli eventi
export const eventVotes = pgTable("event_votes", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  location: integer("location").notNull().default(0),
  selection: integer("selection").notNull().default(0),
  foodBeverage: integer("food_beverage").notNull().default(0),
  organization: integer("organization").notNull().default(0),
  music: integer("music").notNull().default(0),
  atmosphere: integer("atmosphere").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow()
}, (table) => ({
  uniqueEventUser: uniqueIndex("unique_event_user_vote").on(table.eventId, table.userId)
}));

// Tabella per i coupon di sconto degli eventi
export const eventCoupons = pgTable("event_coupons", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  code: varchar("code", { length: 50 }).notNull(),
  discountType: varchar("discount_type", { length: 20 }).notNull().default('percentage'), // 'percentage' o 'fixed'
  discountValue: doublePrecision("discount_value").notNull(),
  allowedCategories: json("allowed_categories").$type<string[]>().default([]), // Categorie a cui è valido il coupon
  isActive: boolean("is_active").default(true),
  maxUses: integer("max_uses").default(null), // Null = illimitato
  usedCount: integer("used_count").default(0),
  validFrom: timestamp("valid_from"), // Null = sempre valido
  validUntil: timestamp("valid_until"), // Null = sempre valido
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow()
}, (table) => ({
  uniqueEventCode: uniqueIndex("unique_event_coupon_code").on(table.eventId, table.code)
}));

// Tabella per i servizi extra degli eventi
export const eventExtraServices = pgTable("event_extra_services", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  price: doublePrecision("price").notNull(), // Prezzo in euro
  allowedCategories: json("allowed_categories").$type<string[]>().default([]), // Categorie a cui è disponibile (vuoto = tutte)
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow()
});

// Tabella per le fasce di prezzo dinamiche (Early Bird, etc.)
export const eventPriceTiers = pgTable("event_price_tiers", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  ticketId: integer("ticket_id").references(() => eventTickets.id, { onDelete: "cascade" }), // null = single-date event
  name: varchar("name", { length: 100 }).notNull(), // "Early Bird", "Standard", "Last Minute"
  categoryPrices: json("category_prices").$type<Record<string, number>>().default({}), // prezzi in centesimi per categoria
  validFrom: timestamp("valid_from").notNull(), // inizio validità di questo tier
  validUntil: timestamp("valid_until").notNull(), // fine validità di questo tier
  sortOrder: integer("sort_order").default(0),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

// Speed Dates table
export const speedDates = pgTable('speed_dates', {
  id: serial('id').primaryKey(),
  organizerId: integer('organizer_id').references(() => users.id, { onDelete: "cascade" }),
  title: varchar('title', { length: 100 }).notNull(),
  description: text('description'),
  date: timestamp('date').notNull(),
  location: varchar('location', { length: 200 }).notNull(),
  city: varchar('city', { length: 100 }).notNull(),
  latitude: varchar('latitude', { length: 50 }).notNull(),
  longitude: varchar('longitude', { length: 50 }).notNull(),
  allowedCategories: json("allowed_categories").$type<string[]>().default([]), // Categories allowed to participate
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow()
});

// Travels table - Travel plans with date ranges
export const travels = pgTable('travels', {
  id: serial('id').primaryKey(),
  organizerId: integer('organizer_id').references(() => users.id, { onDelete: "cascade" }).notNull(),
  description: text('description').notNull(),
  startDate: timestamp('start_date').notNull(),
  endDate: timestamp('end_date').notNull(),
  city: varchar('city', { length: 100 }).notNull(),
  latitude: varchar('latitude', { length: 50 }).notNull(),
  longitude: varchar('longitude', { length: 50 }).notNull(),
  allowedCategories: json("allowed_categories").$type<string[]>().default([]), // Categories that can see this travel
  participants: json("participants").$type<number[]>().default([]), // Array of user IDs interested/participating
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow()
});


// Chat messages table
export const chatMessages = pgTable("chat_messages", {
  id: serial("id").primaryKey(),
  senderId: integer("sender_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  receiverId: integer("receiver_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  isPartner: boolean("is_partner").default(false), // Se il messaggio è stato inviato dal partner (per coppie)
  createdAt: timestamp("created_at").defaultNow()
});

// Insert schema per le foto
export const insertPhotoSchema = createInsertSchema(photos).omit({ id: true, createdAt: true });

export const insertUserSchema = createInsertSchema(users).omit({ id: true });

// Assicuriamoci che telegramId sia richiesto nello schema
// Schema migliorato per telegramId con validazione più flessibile
// Funzione per derivare UserCategory da profile_type e subcategories
function deriveUserCategory(profileType?: ProfileType, subcategories: string[] = []): UserCategory {
  // Se abbiamo già una categoria legacy valida, usala
  // Altrimenti deriva da profile_type
  
  if (profileType === ProfileType.COUPLE) {
    return UserCategory.COUPLE;
  }
  
  // Per gli altri tipi, prova a usare la prima sottocategoria
  const subcategory = subcategories[0];
  
  if (subcategory) {
    // Mappa le sottocategorie ai valori UserCategory
    const categoryMap: Record<string, UserCategory> = {
      'Female': UserCategory.SINGLE_LADY,
      'Male': UserCategory.GENTLEMAN,
      'Dom (M)': UserCategory.DOM_M,
      'Dom (F)': UserCategory.DOM_F,
      'Sub (M)': UserCategory.SUB_M,
      'Sub (F)': UserCategory.SUB_F,
      'Switch (M)': UserCategory.SWITCH_M,
      'Switch (F)': UserCategory.SWITCH_F,
      'Gay': UserCategory.GAY,
      'Lesbian': UserCategory.LESBIAN,
      'Bisexual (M)': UserCategory.BISEXUAL_M,
      'Bisexual (F)': UserCategory.BISEXUAL_F,
      'Transgender': UserCategory.TRANSGENDER,
      'Trans': UserCategory.TRANSGENDER,
      'Trav': UserCategory.TRAV,
    };
    
    if (categoryMap[subcategory]) {
      return categoryMap[subcategory];
    }
  }
  
  // Fallback in base al profile_type
  switch (profileType) {
    case ProfileType.FEMALE:
      return UserCategory.SINGLE_LADY;
    case ProfileType.MALE:
      return UserCategory.GENTLEMAN;
    case ProfileType.TRANS:
      return UserCategory.TRANSGENDER;
    case ProfileType.TRAV:
      return UserCategory.TRAV;
    default:
      return UserCategory.GENTLEMAN;
  }
}

// Schema specifico per la registrazione che accetta l'oggetto location
export const registrationSchema = z.object({
  username: z.string().min(1, 'Username richiesto'),
  telegramId: z.string().min(1, 'Telegram ID richiesto'),
  telegramUsername: z.string().min(1, 'Username Telegram richiesto').refine((val) => val && val.trim() !== '', {
    message: 'Username Telegram obbligatorio per la registrazione'
  }),
  category: z.nativeEnum(UserCategory).optional(), // Reso opzionale - viene derivato automaticamente
  profile_type: z.nativeEnum(ProfileType).optional(),
  subcategories: z.array(z.string()).default([]),
  age: z.number().min(18, 'Età minima 18 anni').max(100, 'Età massima 100 anni').optional(),
  location: z.object({
    city: z.string().min(2, 'Città richiesta'),
    latitude: z.string().min(1, 'Latitudine richiesta'),
    longitude: z.string().min(1, 'Longitudine richiesta')
  }),
  photos: z.array(z.string()).default([]),
  about: z.string().optional(),
  partnerAge: z.number().min(18, 'Età minima partner 18 anni').max(100, 'Età massima partner 100 anni').optional(),
  partnerBirthDate: z.string().optional(),
  birthDate: z.string().optional(),
  interestedCategories: z.array(z.string()).min(1, 'Seleziona almeno una categoria di interesse'),
  telegramLanguage: z.string().optional(),
}).transform((data) => {
  console.log('🔥🔥🔥 REGISTRATION SCHEMA TRANSFORM - CRITICAL DEBUG');
  console.log('🔥🔥🔥 RAW INPUT DATA:', JSON.stringify(data, null, 2));
  console.log('🔥🔥🔥 interestedCategories ANALYSIS:');
  console.log('🔥🔥🔥   - Raw value:', data.interestedCategories);
  console.log('🔥🔥🔥   - Type:', typeof data.interestedCategories);
  console.log('🔥🔥🔥   - Is Array:', Array.isArray(data.interestedCategories));
  console.log('🔥🔥🔥   - Length:', data.interestedCategories?.length);
  console.log('🔥🔥🔥   - Stringified:', JSON.stringify(data.interestedCategories));
  console.log('🔥🔥🔥   - User category:', data.category);

  // Location validation
  if (!data.location || !data.location.city) {
    console.error('🔥🔥🔥 ERRORE: Location mancante!');
    throw new Error('Location è obbligatoria per la registrazione');
  }

  // CRITICAL FIX: Preserve EXACT user selections without any modification
  if (!Array.isArray(data.interestedCategories) || data.interestedCategories.length === 0) {
    console.error('🔥🔥🔥 ❌ CRITICAL ERROR: interestedCategories invalid or empty!');
    console.error('🔥🔥🔥 ❌ Received:', data.interestedCategories);
    console.error('🔥🔥🔥 ❌ Type:', typeof data.interestedCategories);
    console.error('🔥🔥🔥 ❌ Is array:', Array.isArray(data.interestedCategories));
    console.error('🔥🔥🔥 ❌ Length:', data.interestedCategories?.length);
    throw new Error('CRITICAL: Devi selezionare almeno una categoria di interesse');
  }

  // CRITICAL: Use EXACTLY what the user selected - NO defaults, NO modifications
  const exactUserSelections = [...data.interestedCategories];

  console.log('🔥🔥🔥 ✅ PRESERVING USER EXACT SELECTIONS (NO DEFAULTS):');
  console.log('🔥🔥🔥   - User category:', data.category);
  console.log('🔥🔥🔥   - User selected interests:', exactUserSelections);
  console.log('🔥🔥🔥   - Count:', exactUserSelections.length);

  // Log each category for verification
  exactUserSelections.forEach((cat, index) => {
    console.log(`🔥🔥🔥   - Interest ${index}: "${cat}" (type: ${typeof cat})`);
  });

  // Deriva category se non fornito (per retrocompatibilità con nuovo frontend)
  const derivedCategory = data.category || deriveUserCategory(data.profile_type, data.subcategories);
  console.log('🔥🔥🔥   - Derived category:', derivedCategory, '(from:', data.profile_type, data.subcategories, ')');

  // Build final result with preserved categories AS ARRAY (will be serialized in database layer)
  const transformedData = {
    username: data.username,
    telegramId: data.telegramId,
    telegramUsername: data.telegramUsername || null,
    age: data.age || null,
    birthDate: data.birthDate ? new Date(data.birthDate) : null,
    partnerAge: data.partnerAge || null,
    partnerBirthDate: data.partnerBirthDate ? new Date(data.partnerBirthDate) : null,
    category: derivedCategory, // Usa category derivata se non fornita
    profile_type: data.profile_type || null,
    subcategories: data.subcategories || [],
    about: data.about || null,
    photos: data.photos || [],
    city: data.location.city,
    latitude: data.location.latitude,
    longitude: data.location.longitude,
    interests: [],
    isAdmin: false,
    isBanned: false,
    banReason: null,
    visibility_preferences: exactUserSelections, // CRITICAL: EXACT user selections, NO defaults
    language: data.telegramLanguage || 'en'
  };

  console.log('🔥🔥🔥 ✅ TRANSFORMATION COMPLETE:');
  console.log('🔥🔥🔥   - category:', transformedData.category, '(derived from profile_type if not provided)');
  console.log('🔥🔥🔥   - visibility_preferences:', transformedData.visibility_preferences);
  console.log('🔥🔥🔥   - NO category-based defaults applied!');

  return transformedData;
});

export const insertUserSchemaWithRequiredTelegramId = insertUserSchema.extend({
  telegramId: z.preprocess(
    (val) => String(val),
    z.string()
      .min(1, "L'ID Telegram è obbligatorio")
      .transform(val => {
        console.log(`Normalizzando telegramId: "${val}" (${typeof val})`);
        return val.trim();
      })
      .refine(
        (val) => {
          console.log(`Validando telegramId: "${val}" (${typeof val})`);

          if (process.env.NODE_ENV === 'development') {
            return val && val.trim() !== '';
          }

          const isValid = /^\d+$/.test(val) || val.startsWith('@') || val.trim() !== '';
          console.log(`Validazione telegramId: ${isValid ? 'SUCCESSO' : 'FALLIMENTO'}`);
          return isValid;
        },
        {
          message: "L'ID Telegram deve essere un valore valido"
        }
      )
  )
});

// Event schemas
export const insertEventSchema = z.object({
  organizerId: z.number(),
  groupId: z.number().optional().nullable(), // ID del gruppo (opzionale)
  title: z.string().min(1).max(200),
  description: z.string().optional(),
  date: z.string().datetime(), // Accetta stringhe ISO datetime
  location: z.string().min(1),
  city: z.string().min(1),
  latitude: z.string().min(1),
  longitude: z.string().min(1),
  posterUrl: z.string().optional(),
  dresscodeMan: z.string().optional(),
  dresscodeWoman: z.string().optional(),
  allowedCategories: z.array(z.string()).optional(),
  categoryLimits: z.record(z.union([z.number(), z.string(), z.null()])).optional(),
  categoryPrices: z.record(z.number()).optional(),
  // Payment methods
  acceptStripe: z.boolean().optional(),
  // Crypto payment fields
  acceptCryptoPayments: z.boolean().optional(),
  acceptedCryptoTokens: z.array(z.string()).optional(),
  cryptoWalletAddress: z.string().optional().nullable(),
  // Bank transfer
  acceptBankTransfer: z.boolean().optional(),
  // Multi-date events
  isMultiDate: z.boolean().optional(),
});

// Speed date schemas
export const insertSpeedDateSchema = z.object({
  title: z.string().min(1).max(100).optional(),
  description: z.string().optional(),
  date: z.string().datetime(),
  location: z.string().min(2).max(200),
  city: z.string().min(2).max(100),
  latitude: z.string().min(1).max(50),
  longitude: z.string().min(1).max(50),
  maxParticipants: z.number().int().positive().optional(),
});

// Travel schemas
export const insertTravelSchema = z.object({
  organizerId: z.number(),
  description: z.string().min(10),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  city: z.string().min(2).max(100),
  latitude: z.string().min(1).max(50),
  longitude: z.string().min(1).max(50),
  allowedCategories: z.array(z.string()).min(1),
});



export const insertEventParticipationSchema = createInsertSchema(eventParticipations).omit({ id: true, createdAt: true, updatedAt: true });

export const insertEventVoteSchema = createInsertSchema(eventVotes).omit({ id: true, createdAt: true, updatedAt: true });

export const insertEventCouponSchema = createInsertSchema(eventCoupons).omit({ id: true, createdAt: true, updatedAt: true, usedCount: true });

export const insertChatMessageSchema = createInsertSchema(chatMessages).omit({ id: true, createdAt: true });
export const insertTelegramGroupSchema = createInsertSchema(telegramGroups).omit({ id: true, createdAt: true });
export const insertGroupAdmissionRequestSchema = createInsertSchema(groupAdmissionRequests).omit({ id: true, createdAt: true, updatedAt: true });
export const insertTelegramMessageSchema = createInsertSchema(telegramMessages).omit({ id: true, createdAt: true });

// Types
export type User = typeof users.$inferSelect;
// Modifichiamo il tipo InsertUser per usare lo schema che richiede telegramId
export type InsertUser = z.infer<typeof insertUserSchemaWithRequiredTelegramId>;

// Banned/Rejected Users types
export type BannedRejectedUser = typeof bannedRejectedUsers.$inferSelect;
export type InsertBannedRejectedUser = typeof bannedRejectedUsers.$inferInsert;

// Interfaccia utente per il frontend (esclusi campi sensibili o non necessari)
export interface UserFrontend {
  id: number;
  username: string;
  telegramUsername?: string | null;
  age?: number | null;
  partnerAge?: number | null;
  // NEW: Profile Type System
  profile_type?: string | null; // "Couple" | "Female" | "Male" | "Trans" | "Trav"
  subcategories?: string[]; // Max 3 subcategories
  // LEGACY: Old category field (deprecated)
  category: string;
  about?: string | null;
  photo_urls?: string[]; // Array di URL delle foto pubbliche
  profile_photo_url?: string | null; // URL della foto profilo
  interests?: string[];
  isAdmin: boolean;
  isBanned: boolean;
  isPreBanned?: boolean; // Flag pre-ban warning
  preBanReason?: string | null; // Motivo del pre-ban
  preBannedAt?: string | null; // Data del pre-ban
  city?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  language?: string | null;
  lastSeen?: Date | null;
  isOnline?: boolean;
  isInvisible?: boolean; // Aggiunto per il frontend
}


// Event types
export type Event = typeof events.$inferSelect;
export type InsertEvent = z.infer<typeof insertEventSchema>;

export type EventParticipation = typeof eventParticipations.$inferSelect;
export type InsertEventParticipation = z.infer<typeof insertEventParticipationSchema>;

export type EventVote = typeof eventVotes.$inferSelect;
export type InsertEventVote = z.infer<typeof insertEventVoteSchema>;

// Type for EventCoupon
export type EventCoupon = typeof eventCoupons.$inferSelect;
export type InsertEventCoupon = z.infer<typeof insertEventCouponSchema>;

// Types for Event Dates (multi-date events)
export type EventDate = typeof eventDates.$inferSelect;
export type InsertEventDate = typeof eventDates.$inferInsert;

// Types for Event Tickets (multi-date events)
export type EventTicket = typeof eventTickets.$inferSelect;
export type InsertEventTicket = typeof eventTickets.$inferInsert;

// Types for Event Ticket Participations
export type EventTicketParticipation = typeof eventTicketParticipations.$inferSelect;
export type InsertEventTicketParticipation = typeof eventTicketParticipations.$inferInsert;

// Schema for event extra services
export const insertEventExtraServiceSchema = createInsertSchema(eventExtraServices).omit({ id: true, createdAt: true, updatedAt: true });

// Type for EventExtraService
export type EventExtraService = typeof eventExtraServices.$inferSelect;
export type InsertEventExtraService = z.infer<typeof insertEventExtraServiceSchema>;

// Schema for event extra service forms
export const eventExtraServiceSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  price: z.number().min(0),
  allowedCategories: z.array(z.string()).default([])
});

export type EventExtraServiceFormValues = z.infer<typeof eventExtraServiceSchema>;

// Types for EventPriceTier
export type EventPriceTier = typeof eventPriceTiers.$inferSelect;
export type InsertEventPriceTier = typeof eventPriceTiers.$inferInsert;

// Schema for event coupon forms
export const eventCouponSchema = z.object({
  code: z.string().min(2, "Coupon code must be at least 2 characters").max(50, "Coupon code must be less than 50 characters"),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.number().min(0, "Discount value must be positive"),
  allowedCategories: z.array(z.string()).min(1, "Select at least one category"),
  isActive: z.boolean().default(true),
  maxUses: z.number().min(1, "Max uses must be at least 1").nullable().optional(),
  validFrom: z.date().optional().nullable(),
  validUntil: z.date().optional().nullable()
});

export type EventCouponFormValues = z.infer<typeof eventCouponSchema>;

// Type for SpeedDate
export type SpeedDate = typeof speedDates.$inferSelect;
export type InsertSpeedDate = z.infer<typeof insertSpeedDateSchema>;

// Schema for speed date forms
export const speedDateFormSchema = z.object({
  date: z.date({
    required_error: "Please select a date",
  }),
  location: z.object({
    city: z.string().min(1, "Please select a city"),
    latitude: z.string(),
    longitude: z.string(),
  }),
  allowedCategories: z.array(z.string()).min(1, "Please select at least one category"),
  title: z.string().min(3, "Title must be at least 3 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  maxParticipants: z.number().min(2).max(50)
});

export type SpeedDateFormValues = z.infer<typeof speedDateFormSchema>;

// Type for Travel
export type Travel = typeof travels.$inferSelect;
export type InsertTravel = z.infer<typeof insertTravelSchema>;

// Schema for travel forms
export const travelFormSchema = z.object({
  description: z.string().min(10, "Description must be at least 10 characters"),
  dateRange: z.object({
    from: z.date({
      required_error: "Please select a start date",
    }),
    to: z.date({
      required_error: "Please select an end date",
    }),
  }),
  location: z.object({
    city: z.string().min(1, "Please select a city"),
    latitude: z.string(),
    longitude: z.string(),
  }),
  allowedCategories: z.array(z.string()).min(1, "Please select at least one category"),
});

export type TravelFormValues = z.infer<typeof travelFormSchema>;

export type ChatMessage = typeof chatMessages.$inferSelect;
export type InsertChatMessage = z.infer<typeof insertChatMessageSchema>;

export type TelegramGroup = typeof telegramGroups.$inferSelect;
export type InsertTelegramGroup = z.infer<typeof insertTelegramGroupSchema>;

export type GroupAdmissionRequest = typeof groupAdmissionRequests.$inferSelect;
export type InsertGroupAdmissionRequest = z.infer<typeof insertGroupAdmissionRequestSchema>;

export type TelegramMessage = typeof telegramMessages.$inferSelect;
export type InsertTelegramMessage = z.infer<typeof insertTelegramMessageSchema>;

export type Photo = typeof photos.$inferSelect & {
  // Aggiungi dataUrl come campo opzionale per supportare il rendering client-side
  dataUrl?: string;
};
export type InsertPhoto = z.infer<typeof insertPhotoSchema>;



// Add validations and notifications tables
export const validations = pgTable("validations", {
  id: serial("id").primaryKey(),
  requesterId: integer("requester_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  targetUserId: integer("target_user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  validationType: varchar("validation_type", { length: 20 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, confirmed, rejected
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// BDSM Relationships table
export const bdsmRelationships = pgTable("bdsm_relationships", {
  id: serial("id").primaryKey(),
  requesterId: integer("requester_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  targetUserId: integer("target_user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  requesterRole: varchar("requester_role", { length: 100 }).notNull(),
  targetRole: varchar("target_role", { length: 100 }).notNull(),
  requesterPartner: varchar("requester_partner", { length: 10 }), // 'him' | 'her' | null for couples
  targetPartner: varchar("target_partner", { length: 10 }), // 'him' | 'her' | null for couples
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, confirmed, rejected
  contractType: varchar("contract_type", { length: 50 }),
  contractText: text("contract_text"),
  contractStartDate: timestamp("contract_start_date"),
  contractEndDate: timestamp("contract_end_date"),
  contractIsPublic: boolean("contract_is_public").default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => {
  return {
    requesterIdx: index("bdsm_relationships_requester_idx").on(table.requesterId),
    targetIdx: index("bdsm_relationships_target_idx").on(table.targetUserId),
    statusIdx: index("bdsm_relationships_status_idx").on(table.status),
    uniqueRelationship: uniqueIndex("unique_bdsm_relationship").on(table.requesterId, table.targetUserId, table.requesterRole),
  };
});

// Schema per le reazioni degli utenti e foto
export const reactions = pgTable('reactions', {
  id: serial('id').primaryKey(),
  fromUserId: integer('from_user_id').notNull().references(() => users.id, { onDelete: 'cascade' }),
  toUserId: integer('to_user_id').references(() => users.id, { onDelete: 'cascade' }), // Nullable per reazioni foto
  photoId: integer('photo_id').references(() => photos.id, { onDelete: 'cascade' }), // Nullable per reazioni utente
  reactionType: varchar('reaction_type', { length: 50 }).notNull().default('like'),
  comment: varchar('comment', { length: 500 }), // Campo comment opzionale
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => {
  return {
    fromUserIdx: index('reactions_from_user_idx').on(table.fromUserId),
    toUserIdx: index('reactions_to_user_idx').on(table.toUserId),
    photoIdx: index('reactions_photo_idx').on(table.photoId),
  };
});

export const notifications = pgTable("notifications", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  type: varchar("type", { length: 50 }).notNull(), // validation_request, validation_confirmed, etc
  title: varchar("title", { length: 255 }).notNull(),
  message: text("message").notNull(),
  data: text("data"), // JSON string for additional data
  isRead: boolean("is_read").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Insert schemas per validations e notifications
export const insertValidationSchema = createInsertSchema(validations).omit({ id: true, createdAt: true, updatedAt: true });
export const insertBdsmRelationshipSchema = createInsertSchema(bdsmRelationships).omit({ id: true, createdAt: true, updatedAt: true });
export const insertNotificationSchema = createInsertSchema(notifications).omit({ id: true, createdAt: true });

// Types per validations e notifications
export type Validation = typeof validations.$inferSelect;
export type InsertValidation = z.infer<typeof insertValidationSchema>;
export type BdsmRelationship = typeof bdsmRelationships.$inferSelect;
export type InsertBdsmRelationship = z.infer<typeof insertBdsmRelationshipSchema>;
export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;

// Relations
export const telegramGroupsRelations = relations(telegramGroups, ({ one, many }) => ({
  creator: one(users, {
    fields: [telegramGroups.createdBy],
    references: [users.id],
  }),
  messages: many(telegramMessages),
  admissionRequest: one(groupAdmissionRequests, {
    fields: [telegramGroups.id],
    references: [groupAdmissionRequests.createdGroupId],
  }),
}));

export const groupAdmissionRequestsRelations = relations(groupAdmissionRequests, ({ one }) => ({
  requester: one(users, {
    fields: [groupAdmissionRequests.userId],
    references: [users.id],
  }),
  approver: one(users, {
    fields: [groupAdmissionRequests.approvedBy],
    references: [users.id],
  }),
  createdGroup: one(telegramGroups, {
    fields: [groupAdmissionRequests.createdGroupId],
    references: [telegramGroups.id],
  }),
}));

export const telegramMessagesRelations = relations(telegramMessages, ({ one }) => ({
  group: one(telegramGroups, {
    fields: [telegramMessages.groupId],
    references: [telegramGroups.id],
  }),
  user: one(users, {
    fields: [telegramMessages.userId],
    references: [users.id],
  }),
}));



// Chat interactions table - Traccia solo le interazioni tra utenti della mini-app
export const chatInteractions = pgTable("chat_interactions", {
  id: serial("id").primaryKey(),
  user1Id: integer("user1_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  user2Id: integer("user2_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  lastInteractionAt: timestamp("last_interaction_at").notNull().defaultNow(),
  user1LastReadAt: timestamp("user1_last_read_at"), // Ultimo timestamp lettura per user1 (tracking unread)
  user2LastReadAt: timestamp("user2_last_read_at")  // Ultimo timestamp lettura per user2 (tracking unread)
});

// Stato delle richieste di chat
export enum AlbumAccessRequestStatus {
  PENDING = "pending",
  ACCEPTED = "accepted",
  REJECTED = "rejected"
}

// Tabella per le richieste di accesso all'album privato
export const albumAccessRequests = pgTable("album_access_requests", {
  id: serial("id").primaryKey(),
  senderId: integer("sender_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  receiverId: integer("receiver_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  status: text("status", { enum: [AlbumAccessRequestStatus.PENDING, AlbumAccessRequestStatus.ACCEPTED, AlbumAccessRequestStatus.REJECTED] })
    .default(AlbumAccessRequestStatus.PENDING)
    .notNull(),
  message: text("message"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  expiresAt: timestamp("expires_at"), // Quando la scade l'accesso (opzionale)
});

// Insert schema per le richieste di accesso all'album
export const insertAlbumAccessRequestSchema = createInsertSchema(albumAccessRequests).omit({ id: true, createdAt: true, updatedAt: true });
export type AlbumAccessRequest = typeof albumAccessRequests.$inferSelect;
export type InsertAlbumAccessRequest = z.infer<typeof insertAlbumAccessRequestSchema>;

// Tabella per le richieste di chat
export const chatRequests = pgTable("chat_requests", {
  id: serial("id").primaryKey(),
  senderId: integer("sender_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  receiverId: integer("receiver_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  status: text("status", { enum: [AlbumAccessRequestStatus.PENDING, AlbumAccessRequestStatus.ACCEPTED, AlbumAccessRequestStatus.REJECTED] })
    .default(AlbumAccessRequestStatus.PENDING)
    .notNull(),
  message: text("message"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow()
});

// Insert schema per le interazioni chat
export const insertChatInteractionSchema = createInsertSchema(chatInteractions).omit({ id: true });
export type ChatInteraction = typeof chatInteractions.$inferSelect;
export type InsertChatInteraction = z.infer<typeof insertChatInteractionSchema>;

// Insert schema per le richieste di chat
export const insertChatRequestSchema = createInsertSchema(chatRequests).omit({ id: true, createdAt: true, updatedAt: true });
export type ChatRequest = typeof chatRequests.$inferSelect;
export type InsertChatRequest = z.infer<typeof insertChatRequestSchema>;

// Relazioni per le foto
export const photosRelations = relations(photos, ({ one }) => ({
  user: one(users, {
    fields: [photos.userId],
    references: [users.id]
  })
}));

// Relazioni per le richieste di accesso all'album
export const albumAccessRequestsRelations = relations(albumAccessRequests, ({ one }) => ({
  sender: one(users, {
    fields: [albumAccessRequests.senderId],
    references: [users.id],
    relationName: "userSentAlbumRequests"
  }),
  receiver: one(users, {
    fields: [albumAccessRequests.receiverId],
    references: [users.id],
    relationName: "userReceivedAlbumRequests"
  })
}));

export const usersRelations = relations(users, ({ many }) => ({
  telegramGroups: many(telegramGroups),
  telegramMessages: many(telegramMessages),
  photos: many(photos),
  chatInteractions1: many(chatInteractions, { relationName: "userChatInteractions1" }),
  chatInteractions2: many(chatInteractions, { relationName: "userChatInteractions2" }),
  sentChatRequests: many(chatRequests, { relationName: "userSentChatRequests" }),
  receivedChatRequests: many(chatRequests, { relationName: "userReceivedChatRequests" }),
  sentAlbumRequests: many(albumAccessRequests, { relationName: "userSentAlbumRequests" }),
  receivedAlbumRequests: many(albumAccessRequests, { relationName: "userReceivedAlbumRequests" }),
  profileViewsReceived: many(profileViews, { relationName: "userProfileViewsReceived" }),
  profileViewsMade: many(profileViews, { relationName: "userProfileViewsMade" }),
  requestedValidations: many(validations, { relationName: "userRequestedValidations" }),
  receivedValidations: many(validations, { relationName: "userReceivedValidations" }),
  requestedBdsmRelationships: many(bdsmRelationships, { relationName: "userRequestedBdsmRelationships" }),
  receivedBdsmRelationships: many(bdsmRelationships, { relationName: "userReceivedBdsmRelationships" }),
  reactionsSent: many(reactions, { relationName: "userReactionsSent" }),
  reactionsReceived: many(reactions, { relationName: "userReactionsReceived" }),
  wallPosts: many(wallPosts, { relationName: "userWallPosts" }),
  wallVotes: many(wallVotes, { relationName: "userWallVotes" })
}));

export const chatInteractionsRelations = relations(chatInteractions, ({ one }) => ({
  user1: one(users, {
    fields: [chatInteractions.user1Id],
    references: [users.id],
    relationName: "userChatInteractions1"
  }),
  user2: one(users, {
    fields: [chatInteractions.user2Id],
    references: [users.id],
    relationName: "userChatInteractions2"
  })
}));

export const chatRequestsRelations = relations(chatRequests, ({ one }) => ({
  sender: one(users, {
    fields: [chatRequests.senderId],
    references: [users.id],
    relationName: "userSentChatRequests"
  }),
  receiver: one(users, {
    fields: [chatRequests.receiverId],
    references: [users.id],
    relationName: "userReceivedChatRequests"
  })
}));

// Tabella per le visualizzazioni dei profili - chi visita il tuo profilo
export const profileViews = pgTable("profile_views", {
  id: serial("id").primaryKey(),
  viewedUserId: integer("viewed_user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // L'utente il cui profilo è stato visitato
  viewerUserId: integer("viewer_user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // L'utente che ha visitato il profilo
  viewedAt: timestamp("viewed_at").defaultNow().notNull()
});

// Insert schema per le visualizzazioni profilo
export const insertProfileViewSchema = createInsertSchema(profileViews).omit({ id: true, viewedAt: true });
export type ProfileView = typeof profileViews.$inferSelect;
export type InsertProfileView = z.infer<typeof insertProfileViewSchema>;

// Relazioni per le visualizzazioni profilo
export const profileViewsRelations = relations(profileViews, ({ one }) => ({
  viewedUser: one(users, {
    fields: [profileViews.viewedUserId],
    references: [users.id],
    relationName: "userProfileViewsReceived"
  }),
  viewerUser: one(users, {
    fields: [profileViews.viewerUserId],
    references: [users.id],
    relationName: "userProfileViewsMade"
  })
}));

// Relazioni per le validations
export const validationsRelations = relations(validations, ({ one }) => ({
  requester: one(users, {
    fields: [validations.requesterId],
    references: [users.id],
    relationName: "userRequestedValidations"
  }),
  targetUser: one(users, {
    fields: [validations.targetUserId],
    references: [users.id],
    relationName: "userReceivedValidations"
  })
}));

// Relazioni per BDSM relationships
export const bdsmRelationshipsRelations = relations(bdsmRelationships, ({ one }) => ({
  requester: one(users, {
    fields: [bdsmRelationships.requesterId],
    references: [users.id],
    relationName: "userRequestedBdsmRelationships"
  }),
  targetUser: one(users, {
    fields: [bdsmRelationships.targetUserId],
    references: [users.id],
    relationName: "userReceivedBdsmRelationships"
  })
}));

// Relazioni per le reactions
export const reactionsRelations = relations(reactions, ({ one }) => ({
  fromUser: one(users, {
    fields: [reactions.fromUserId],
    references: [users.id],
    relationName: "userReactionsSent"
  }),
  toUser: one(users, {
    fields: [reactions.toUserId],
    references: [users.id],
    relationName: "userReactionsReceived"
  })
}));

// Relazione many-to-many tra utenti e gruppi Telegram
export const userTelegramGroups = pgTable("user_telegram_groups", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  groupId: integer("group_id").notNull().references(() => telegramGroups.id, { onDelete: "cascade" }),
  joinedAt: timestamp("joined_at").defaultNow()
});

// Insert schema per la relazione utente-gruppo
export const insertUserTelegramGroupSchema = createInsertSchema(userTelegramGroups).omit({ id: true, joinedAt: true });

// Stripe Transactions table - Track all Stripe payments
export const stripeTransactions = pgTable("stripe_transactions", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  paymentIntentId: text("payment_intent_id").notNull().unique(),
  amountGross: integer("amount_gross").notNull(), // Importo lordo in centesimi
  amountNet: integer("amount_net").notNull(), // Importo netto incassato dall'organizzatore
  stripeFee: integer("stripe_fee").notNull(), // Commissione Stripe
  platformFee: integer("platform_fee").notNull(), // Commissione piattaforma (1%)
  currency: varchar("currency", { length: 3 }).notNull().default("eur"),
  status: varchar("status", { length: 50 }).notNull(), // succeeded, pending, failed
  stripeAccountId: text("stripe_account_id"), // Account Connect dell'organizzatore
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow()
}, (table) => ({
  eventIdx: index("idx_stripe_transactions_event_id").on(table.eventId),
  userIdx: index("idx_stripe_transactions_user_id").on(table.userId),
  paymentIntentIdx: index("idx_stripe_transactions_payment_intent").on(table.paymentIntentId),
  statusIdx: index("idx_stripe_transactions_status").on(table.status)
}));

// Insert schema per stripe transactions
export const insertStripeTransactionSchema = createInsertSchema(stripeTransactions).omit({ id: true, createdAt: true, updatedAt: true });

// Types
export type StripeTransaction = typeof stripeTransactions.$inferSelect;
export type InsertStripeTransaction = z.infer<typeof insertStripeTransactionSchema>;

// Payment Sessions table - Temporary sessions for TicketGram payment flow
export const paymentSessions = pgTable("payment_sessions", {
  id: serial("id").primaryKey(),
  token: varchar("token", { length: 64 }).notNull().unique(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  category: varchar("category", { length: 100 }).notNull(),
  ticketId: integer("ticket_id"),
  couponCode: varchar("coupon_code", { length: 100 }),
  amount: integer("amount").notNull(), // grossAmount in cents
  currency: varchar("currency", { length: 10 }).notNull().default("eur"),
  connectedAccountId: text("connected_account_id").notNull(),
  publishableKey: text("publishable_key").notNull(),
  clientSecret: text("client_secret"),
  paymentIntentId: text("payment_intent_id"),
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, paying, paid, failed, expired
  pricing: jsonb("pricing"), // Full server pricing object
  metadata: jsonb("metadata"), // Payment metadata for Stripe
  description: text("description"), // Payment description
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow()
}, (table) => ({
  tokenIdx: index("idx_payment_sessions_token").on(table.token),
  userIdx: index("idx_payment_sessions_user_id").on(table.userId),
  statusIdx: index("idx_payment_sessions_status").on(table.status)
}));

export type PaymentSession = typeof paymentSessions.$inferSelect;

// TON Transactions table - Track all TON cryptocurrency payments
export const tonTransactions = pgTable("ton_transactions", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),

  // TON blockchain identifiers
  transactionHash: text("transaction_hash").notNull().unique(),
  walletAddress: text("wallet_address").notNull(),

  // Amounts (in smallest units)
  amountEurCents: integer("amount_eur_cents").notNull(), // Original EUR price in cents
  amountTonNano: text("amount_ton_nano").notNull(), // TON amount in nanotons (BigInt as string)
  amountReceivedNano: text("amount_received_nano"), // Actual amount received after fees

  // Exchange rate tracking
  eurToTonRate: text("eur_to_ton_rate").notNull(), // Store as string for precision
  rateTimestamp: timestamp("rate_timestamp").notNull(),
  rateSource: varchar("rate_source", { length: 50 }).notNull(),

  // Fee tracking (for loss analysis)
  blockchainFeeNano: text("blockchain_fee_nano"),
  totalLossEurCents: integer("total_loss_eur_cents"), // Total loss from EUR amount

  // Transaction status
  status: varchar("status", { length: 50 }).notNull(), // pending, confirmed, failed, expired
  confirmations: integer("confirmations").default(0),

  // Timestamps
  createdAt: timestamp("created_at").defaultNow(),
  confirmedAt: timestamp("confirmed_at"),
  expiresAt: timestamp("expires_at"), // Payment window expiration (15 min)

  // Metadata
  currency: varchar("currency", { length: 3 }).default("eur"),
  tonNetwork: varchar("ton_network", { length: 20 }).default("mainnet"), // 'mainnet' or 'testnet'
  category: text("category"), // Ticket category purchased
  tokenType: varchar("token_type", { length: 10 }).notNull().default("usdt") // 'usdt' | 'usdc' | 'ton'
}, (table) => ({
  eventIdx: index("idx_ton_transactions_event_id").on(table.eventId),
  userIdx: index("idx_ton_transactions_user_id").on(table.userId),
  statusIdx: index("idx_ton_transactions_status").on(table.status),
  walletIdx: index("idx_ton_transactions_wallet").on(table.walletAddress)
}));

// Insert schema per ton transactions
export const insertTonTransactionSchema = createInsertSchema(tonTransactions).omit({ id: true, createdAt: true, confirmedAt: true });

// Types
export type TonTransaction = typeof tonTransactions.$inferSelect;
export type InsertTonTransaction = z.infer<typeof insertTonTransactionSchema>;

// Partner type for couple accounts - 'single' for non-couples, 'him'/'her' for couples
export const partnerTypeEnum = z.enum(['single', 'him', 'her']);
export type PartnerType = z.infer<typeof partnerTypeEnum>;

// Kinky Test Results table - Stores user test results with dimensional scores
export const kinkyTestResults = pgTable("kinky_test_results", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  partner: varchar("partner", { length: 10 }).notNull().default("single"), // 'single' | 'him' | 'her'
  testVersion: varchar("test_version", { length: 10 }).notNull().default("v1.0"),
  completedAt: timestamp("completed_at").defaultNow().notNull(),

  // BDSM Dynamics Scores (0-100)
  dominantScore: integer("dominant_score").notNull().default(0),
  submissiveScore: integer("submissive_score").notNull().default(0),
  switchScore: integer("switch_score").notNull().default(0),
  sadistScore: integer("sadist_score").notNull().default(0),
  masochistScore: integer("masochist_score").notNull().default(0),
  riggerScore: integer("rigger_score").notNull().default(0),
  ropeBunnyScore: integer("rope_bunny_score").notNull().default(0),
  bratScore: integer("brat_score").notNull().default(0),
  bratTamerScore: integer("brat_tamer_score").notNull().default(0),
  samScore: integer("sam_score").notNull().default(0),
  primalScore: integer("primal_score").notNull().default(0),
  primalPreyScore: integer("primal_prey_score").notNull().default(0),
  voyeurScore: integer("voyeur_score").notNull().default(0),
  exhibitionistScore: integer("exhibitionist_score").notNull().default(0),
  degraderScore: integer("degrader_score").notNull().default(0),
  degradeeScore: integer("degradee_score").notNull().default(0),

  // Non-Monogamy Style Scores (0-100)
  swingerScore: integer('swinger_score').notNull().default(0),
  fullSwapScore: integer('full_swap_score').notNull().default(0),
  softSwapScore: integer('soft_swap_score').notNull().default(0),
  cuckoldScore: integer('cuckold_score').notNull().default(0),
  femaleOnlyScore: integer('female_only_score').notNull().default(0),
  polyamorousScore: integer('polyamorous_score').notNull().default(0),
  openRelationshipScore: integer('open_relationship_score').notNull().default(0),
  monogamishScore: integer('monogamish_score').notNull().default(0),

  // Additional Non-Monogamy Scores (0-100)
  bicuriousGroupScore: integer("bicurious_group_score").notNull().default(0),
  groupPlayScore: integer("group_play_score").notNull().default(0),

  // Aftercare Score (BDSM) (0-100)
  aftercareScore: integer("aftercare_score").notNull().default(0),

  // Kink Intensity (0-100, 0=vanilla, 100=extreme)
  kinkIntensity: integer("kink_intensity").notNull().default(0),
  vanillaScore: integer("vanilla_score").notNull().default(0),

  // 5 Core Dimensions (0-100)
  dominanceScore: integer("dominance_score").notNull().default(0),
  submissionScore: integer("submission_score").notNull().default(0),
  libertinoScore: integer("libertino_score").notNull().default(0),
  transgressiveScore: integer("transgressive_score").notNull().default(0),

  // Derived badges/roles (top traits)
  bdsmRoles: json("bdsm_roles").$type<string[]>().default([]),
  swingerStyles: json("swinger_styles").$type<string[]>().default([]),
  polyStyles: json("poly_styles").$type<string[]>().default([]),
  topKinks: json("top_kinks").$type<string[]>().default([]),

  // Full raw answers for future analysis
  rawAnswers: json("raw_answers").$type<Record<string, number>>().default({}),

  // Aggregate macro-category scores for radar chart
  bdsmTotalScore: integer("bdsm_total_score").notNull().default(0),
  nonMonogamyTotalScore: integer("non_monogamy_total_score").notNull().default(0),

  updatedAt: timestamp("updated_at").defaultNow()
}, (table) => ({
  userIdIdx: index("kinky_test_results_user_id_idx").on(table.userId),
  userPartnerUnique: uniqueIndex("kinky_test_results_user_partner_unique").on(table.userId, table.partner)
}));

// Insert schema for kinky test results
export const insertKinkyTestResultSchema = createInsertSchema(kinkyTestResults).omit({ id: true, completedAt: true, updatedAt: true });

// Types for kinky test
export type KinkyTestResult = typeof kinkyTestResults.$inferSelect & {
  dominanceScore: number;
  submissionScore: number;
  libertinoScore: number;
  transgressiveScore: number;
};
export type InsertKinkyTestResult = typeof kinkyTestResults.$inferInsert;

// Kinky Test Answer Schema (for API validation)
export const kinkyTestAnswersSchema = z.object({
  answers: z.record(z.string(), z.number().min(1).max(5))
});

export type KinkyTestAnswers = z.infer<typeof kinkyTestAnswersSchema>;

// Kinky Test Results Relations
export const kinkyTestResultsRelations = relations(kinkyTestResults, ({ one }) => ({
  user: one(users, {
    fields: [kinkyTestResults.userId],
    references: [users.id],
    relationName: "userKinkyTestResult"
  })
}));

// Guest Telegram Visits table - Track non-registered users visiting the app
export const guestTelegramVisits = pgTable("guest_telegram_visits", {
  telegramId: text("telegram_id").primaryKey(), // Telegram ID as primary key
  telegramUsername: text("telegram_username"),
  firstName: text("first_name").notNull(),
  lastName: text("last_name"),
  visitCount: integer("visit_count").notNull().default(1),
  firstVisit: timestamp("first_visit").defaultNow().notNull(),
  lastVisit: timestamp("last_visit").defaultNow().notNull()
}, (table) => ({
  lastVisitIdx: index("idx_guest_last_visit").on(table.lastVisit),
  visitCountIdx: index("idx_guest_visit_count").on(table.visitCount)
}));

// Insert schema for guest visits
export const insertGuestTelegramVisitSchema = createInsertSchema(guestTelegramVisits).omit({ firstVisit: true, lastVisit: true });

// Types for guest visits
export type GuestTelegramVisit = typeof guestTelegramVisits.$inferSelect;
export type InsertGuestTelegramVisit = z.infer<typeof insertGuestTelegramVisitSchema>;

// Wall Content Types
export enum WallContentType {
  TWEET = "tweet",
  POST = "post"
}

// Wall Posts table - User-generated content with 7-day expiration
export const wallPosts = pgTable("wall_posts", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  contentType: text("content_type", { enum: [WallContentType.TWEET, WallContentType.POST] }).notNull(),

  // Content fields
  title: text("title"), // Optional for tweets, required for posts
  content: text("content").notNull(), // Text for tweets/posts
  imageUrl: text("image_url"), // Optional image for posts
  videoUrl: text("video_url"), // Optional video URL (Telegram CDN)
  mentionedUserIds: json("mentioned_user_ids").$type<number[]>().default([]),

  // Voting and ranking
  upvotes: integer("upvotes").notNull().default(0),
  downvotes: integer("downvotes").notNull().default(0),
  hotScore: doublePrecision("hot_score").notNull().default(0), // Reddit-style hot ranking score

  // Expiration and archiving
  createdAt: timestamp("created_at").defaultNow().notNull(),
  expiresAt: timestamp("expires_at").notNull(), // Auto-set to createdAt + 7 days
  isArchived: boolean("is_archived").default(false), // True when content expires (for posts, moved to profile)

  // Visibility control
  isPublic: boolean("is_public").notNull().default(true), // True = visible to all, False = visible only to users with matching categories

  updatedAt: timestamp("updated_at").defaultNow()
}, (table) => ({
  userIdIdx: index("wall_posts_user_id_idx").on(table.userId),
  contentTypeIdx: index("wall_posts_content_type_idx").on(table.contentType),
  hotScoreIdx: index("wall_posts_hot_score_idx").on(table.hotScore), // For efficient ranking queries
  expiresAtIdx: index("wall_posts_expires_at_idx").on(table.expiresAt), // For cleanup jobs
  isArchivedIdx: index("wall_posts_is_archived_idx").on(table.isArchived),
  isPublicIdx: index("wall_posts_is_public_idx").on(table.isPublic) // For visibility filtering
}));

// Wall Votes table - Track user upvotes/downvotes (Reddit-style)
export const wallVotes = pgTable("wall_votes", {
  id: serial("id").primaryKey(),
  postId: integer("post_id").notNull().references(() => wallPosts.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  voteType: varchar("vote_type", { length: 4 }).notNull(), // 'up' or 'down'
  createdAt: timestamp("created_at").defaultNow().notNull()
}, (table) => ({
  postIdIdx: index("wall_votes_post_id_idx").on(table.postId),
  userIdIdx: index("wall_votes_user_id_idx").on(table.userId),
  uniqueUserPost: uniqueIndex("wall_votes_unique_user_post").on(table.postId, table.userId) // One vote per user per post
}));

// Insert schemas for wall
export const insertWallPostSchema = createInsertSchema(wallPosts).omit({ id: true, createdAt: true, updatedAt: true, hotScore: true });
export const insertWallVoteSchema = createInsertSchema(wallVotes).omit({ id: true, createdAt: true });

// Types for wall
export type WallPost = typeof wallPosts.$inferSelect;
export type InsertWallPost = z.infer<typeof insertWallPostSchema>;
export type WallVote = typeof wallVotes.$inferSelect;
export type InsertWallVote = z.infer<typeof insertWallVoteSchema>;

// Relations for wall posts
export const wallPostsRelations = relations(wallPosts, ({ one, many }) => ({
  user: one(users, {
    fields: [wallPosts.userId],
    references: [users.id],
    relationName: "userWallPosts"
  }),
  votes: many(wallVotes, { relationName: "postWallVotes" }),
  comments: many(wallComments, { relationName: "postWallComments" })
}));

// Relations for wall votes
export const wallVotesRelations = relations(wallVotes, ({ one }) => ({
  post: one(wallPosts, {
    fields: [wallVotes.postId],
    references: [wallPosts.id],
    relationName: "postWallVotes"
  }),
  user: one(users, {
    fields: [wallVotes.userId],
    references: [users.id],
    relationName: "userWallVotes"
  })
}));

// Wall Comments table - Comments on wall posts
export const wallComments = pgTable("wall_comments", {
  id: serial("id").primaryKey(),
  postId: integer("post_id").notNull().references(() => wallPosts.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  parentCommentId: integer("parent_comment_id").references((): AnyPgColumn => wallComments.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  mentionedUserIds: json("mentioned_user_ids").$type<number[]>().default([]),
  createdAt: timestamp("created_at").defaultNow().notNull()
}, (table) => ({
  postIdIdx: index("wall_comments_post_id_idx").on(table.postId),
  userIdIdx: index("wall_comments_user_id_idx").on(table.userId),
  createdAtIdx: index("wall_comments_created_at_idx").on(table.createdAt),
  parentIdIdx: index("wall_comments_parent_id_idx").on(table.parentCommentId)
}));

// Insert schema for wall comments
export const insertWallCommentSchema = createInsertSchema(wallComments).omit({ id: true, createdAt: true });

// Types for wall comments
export type WallComment = typeof wallComments.$inferSelect;
export type InsertWallComment = z.infer<typeof insertWallCommentSchema>;

// Relations for wall comments
export const wallCommentsRelations = relations(wallComments, ({ one, many }) => ({
  post: one(wallPosts, {
    fields: [wallComments.postId],
    references: [wallPosts.id],
    relationName: "postWallComments"
  }),
  user: one(users, {
    fields: [wallComments.userId],
    references: [users.id],
    relationName: "userWallComments"
  }),
  parentComment: one(wallComments, {
    fields: [wallComments.parentCommentId],
    references: [wallComments.id],
    relationName: "commentReplies"
  }),
  replies: many(wallComments, { relationName: "commentReplies" })
}));

// ===== EVENT COMMENTS =====

export const eventTopics = pgTable("event_topics", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description"),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow().notNull()
}, (table) => ({
  eventIdIdx: index("event_topics_event_id_idx").on(table.eventId),
  userIdIdx: index("event_topics_user_id_idx").on(table.userId)
}));

export const insertEventTopicSchema = createInsertSchema(eventTopics).omit({ id: true, createdAt: true });
export type EventTopic = typeof eventTopics.$inferSelect;
export type InsertEventTopic = z.infer<typeof insertEventTopicSchema>;

export const eventTopicsRelations = relations(eventTopics, ({ one, many }) => ({
  event: one(events, { fields: [eventTopics.eventId], references: [events.id], relationName: "eventTopics" }),
  user: one(users, { fields: [eventTopics.userId], references: [users.id], relationName: "userEventTopics" }),
  comments: many(eventComments, { relationName: "topicComments" })
}));

export const eventComments = pgTable("event_comments", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  topicId: integer("topic_id").references(() => eventTopics.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  parentCommentId: integer("parent_comment_id").references((): AnyPgColumn => eventComments.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  mentionedUserIds: json("mentioned_user_ids").$type<number[]>().default([]),
  createdAt: timestamp("created_at").defaultNow().notNull()
}, (table) => ({
  eventIdIdx: index("event_comments_event_id_idx").on(table.eventId),
  topicIdIdx: index("event_comments_topic_id_idx").on(table.topicId),
  userIdIdx: index("event_comments_user_id_idx").on(table.userId),
  createdAtIdx: index("event_comments_created_at_idx").on(table.createdAt),
  parentIdIdx: index("event_comments_parent_id_idx").on(table.parentCommentId)
}));

export const insertEventCommentSchema = createInsertSchema(eventComments).omit({ id: true, createdAt: true });
export type EventComment = typeof eventComments.$inferSelect;
export type InsertEventComment = z.infer<typeof insertEventCommentSchema>;

export const eventCommentsRelations = relations(eventComments, ({ one, many }) => ({
  event: one(events, { fields: [eventComments.eventId], references: [events.id], relationName: "eventComments" }),
  topic: one(eventTopics, { fields: [eventComments.topicId], references: [eventTopics.id], relationName: "topicComments" }),
  user: one(users, { fields: [eventComments.userId], references: [users.id], relationName: "userEventComments" }),
  parentComment: one(eventComments, { fields: [eventComments.parentCommentId], references: [eventComments.id], relationName: "commentReplies" }),
  replies: many(eventComments, { relationName: "commentReplies" })
}));
